import { useMemo, useState } from 'react';
import { Play, Loader2, Terminal, BoomBox, AlertTriangle, Clock } from 'lucide-react';
import { useRunSandboxTestMutation } from '@/store/api/communicationApi';
import type { SandboxTestResult, OutcomeEvent } from '@/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { isValidJs } from './WebhookForm';

interface SandboxTestPanelProps {
  webhookId: string;
  /**
   * The stored handler code. When provided, it is syntax-gated before the
   * test-run POST so legacy invalid webhooks fail fast with a clear inline
   * error instead of a backend parse failure.
   */
  code?: string;
}

const sampleInput = `{
  "event": "file.created",
  "payload": {
    "fileName": "report.pdf",
    "size": 1048576
  }
}`;

function OutcomeBadge({ event }: { event: OutcomeEvent }) {
  return (
    <Badge variant={event.kind === 'http' ? 'secondary' : 'outline'} className="text-xs font-mono">
      {event.kind === 'http' ? `${event.method} ${event.url}` : `${event.service} · ${event.method}`}
    </Badge>
  );
}

export function SandboxTestPanel({ webhookId, code }: SandboxTestPanelProps) {
  const [runTest, { isLoading }] = useRunSandboxTestMutation();
  const [sample, setSample] = useState(sampleInput);
  const [result, setResult] = useState<SandboxTestResult | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  const parsed: { ok: boolean; value?: unknown; error?: string } = useMemo(() => {
    if (!sample.trim()) return { ok: true, value: undefined };
    // Strip Markdown code fences / surrounding whitespace so JSON is easy to paste.
    const cleaned = sample.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
    try {
      return { ok: true, value: JSON.parse(cleaned) };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  }, [sample]);

  const handleRun = async () => {
    // F3: gate the test-run on the sandbox syntax contract too — a webhook
    // whose stored code is not a valid script body can never run (legacy
    // invalid webhooks fail fast here with a clear message).
    if (code !== undefined && !isValidJs(code)) {
      setInputError(
        'Stored handler code is not valid JavaScript and cannot be test-run. Edit the webhook to fix its handler code.',
      );
      return;
    }
    if (!parsed.ok) {
      setInputError(parsed.error ? `Invalid JSON: ${parsed.error}` : 'Sample input must be valid JSON.');
      return;
    }
    setInputError(null);
    try {
      const res = await runTest({ id: webhookId, sampleInput: parsed.value }).unwrap();
      setResult(res);
    } catch {
      setResult(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Sample input */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="sandbox-sample">Sample input (JSON)</Label>
          <Button type="button" size="sm" onClick={handleRun} disabled={isLoading} className="gap-1.5">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run test
          </Button>
        </div>
        <textarea
          id="sandbox-sample"
          value={sample}
          onChange={(e) => setSample(e.target.value)}
          rows={8}
          className={cn(
            'w-full resize-none rounded-md border bg-background px-3 py-2 font-mono text-sm leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            inputError ? 'border-destructive' : 'border-input',
          )}
          spellCheck={false}
        />
        {inputError && <p className="text-xs text-destructive">{inputError}</p>}
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-4 rounded-md border p-4">
          {/* Summary */}
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={result.success ? 'success' : 'destructive'}>
              {result.success ? 'Success' : 'Failed'}
            </Badge>
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {result.durationMs}ms
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <BoomBox className="h-3.5 w-3.5" /> {(result.emits ?? []).length} emits
            </span>
          </div>

          {result.error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{result.error.kind}</p>
                <p className="text-sm opacity-90">{result.error.message}</p>
              </div>
            </div>
          )}

          {/* Emits */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Emits</h4>
            {(result.emits ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No events emitted.</p>
            ) : (
              <ul className="space-y-1.5">
                {(result.emits ?? []).map((e, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">#{i + 1}</span>
                    <OutcomeBadge event={e} />
                    {(e.kind === 'http' ? e.body : e.data) !== undefined && (
                      <span className="font-mono text-xs text-muted-foreground truncate">
                        {JSON.stringify(e.kind === 'http' ? e.body : e.data)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Console output */}
          <div className="space-y-1.5">
            <h4 className="flex items-center gap-1.5 text-sm font-semibold">
              <Terminal className="h-4 w-4" /> Console output
            </h4>
            <pre className="max-h-48 overflow-auto rounded-md bg-muted/60 p-3 font-mono text-xs leading-relaxed">
                {(result.consoleOutput ?? '').trim() ? result.consoleOutput : '(no output)'}
              </pre>
          </div>

          {/* Raw result */}
          <div>
            <h4 className="mb-1 text-sm font-semibold">Full result</h4>
            <JsonViewer data={result} />
          </div>
        </div>
      )}
    </div>
  );
}